import { forwardRef, useMemo } from 'react';
import { css, Global } from '@emotion/react';
import { HTMLMotionProps, motion } from 'motion/react';
import { useThemeValues } from '../../../hooks';
import { ModalBoxProps } from '../atoms';
import { useModalContext } from '../provider';
import { ResponsiveModalBox } from './responsive-modal-box';
import { useResponsiveModal } from './responsive-modal.context';

export type ResponsiveModalBodyProps = ModalBoxProps;

/**
 * Equivalent to `CenteredModalBox` when !isResponsiveMode (inside <Modal.Responsive>)
 * Otherwise, equivalent to full screen / mobile modal.
 */
export const ResponsiveModalBody = forwardRef<HTMLElement, ResponsiveModalBodyProps>((props, ref) => {
  const { isResponsiveMode, topOffset } = useResponsiveModal();
  const { containerProps } = useModalContext();
  const { zIndex, borderRadius, spacing } = useThemeValues();

  const motionStyles = useMemo(() => {
    return getBodyMotionStyles({ isResponsiveMode });
  }, [isResponsiveMode]);

  const bodyContainerProps = isResponsiveMode ? containerProps : {};

  const getBodyStyles = () => {
    if (isResponsiveMode) {
      return css`
        width: 100%;
        height: calc(100% - ${topOffset}px);
        position: absolute;
        top: ${topOffset}px;
        left: 0;
        z-index: ${zIndex.modals};
      `;
    }

    return css``;
  };

  const outerBoxStyles = useMemo(() => {
    return getBodyStyles();
  }, [isResponsiveMode, topOffset, zIndex, borderRadius.medium, spacing]);

  return (
    <>
      <GlobalStyles isResponsiveMode={isResponsiveMode} />
      <motion.section
        className='responsive-modal__body'
        {...motionStyles}
        {...bodyContainerProps}
        css={outerBoxStyles}
        ref={ref}
      >
        <ResponsiveModalBox {...props} />
      </motion.section>
    </>
  );
});

ResponsiveModalBody.displayName = 'ResponsiveModalBody';

const getBodyMotionStyles = ({ isResponsiveMode }: { isResponsiveMode: boolean }): HTMLMotionProps<'section'> => {
  if (isResponsiveMode) {
    return {
      variants: {
        hidden: {
          y: '100%',
        },
        visible: {
          y: '0%',
        },
        exit: {
          y: '100%',
        },
      },
      initial: 'hidden',
      animate: 'visible',
      exit: 'exit',
      transition: { type: 'keyframes', stiffness: 260, damping: 20 },
    };
  }

  return {
    initial: {
      opacity: 0,
      y: '50%',
    },
    animate: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      y: '50%',
    },
    transition: {
      type: 'spring',
      damping: 24,
      stiffness: 300,
    },
  };
};
const GlobalStyles = ({ isResponsiveMode }: { isResponsiveMode: boolean }) => {
  const { zIndex } = useThemeValues();
  if (isResponsiveMode) {
    return null;
  }
  return (
    <Global
      styles={css`
        .wv-modal-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          top: 0;
          left: 0;
          z-index: ${zIndex.modals};

          :focus {
            outline: none;
          }
        }
      `}
    />
  );
};
